import { addHours, isWithinInterval } from 'date-fns';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import placeholderURL from 'assets/cards/placeholder.svg';
import { Button } from 'atoms/buttons/Button';
import { Vertical } from 'atoms/layout/flex';
import { Caption, LabelS, Text16 } from 'atoms/typography';
import { CardImg } from 'components/card/CardImg';
import { useTickerContext } from 'contexts/ticker';
import TimeLeft from 'contexts/ticker/TimeLeft';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useIsMobileApp } from 'hooks/useIsMobileApp';
import { glossary } from 'lib/glossary';
import { MonetaryAmountParams } from 'lib/monetaryAmount';

import { MissingCard } from './MissingCard';

const EMPTY = '– –';

const StyledLink = styled(Vertical)`
  align-items: center;
  justify-content: stretch;
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--double-unit);
  &:hover,
  &:focus {
    color: inherit;
  }
`;

const CardImgWrapper = styled.div`
  position: relative;
  flex: 1;
  padding: var(--triple-unit);
  filter: brightness(50%);
  transition: filter 0.2s ease-in-out;
`;

const Wrapper = styled.div`
  position: relative;
  aspect-ratio: var(--card-aspect-ratio);
  width: 100%;
  border: 1px dashed var(--c-neutral-400);
  border-radius: inherit;
  padding: var(--double-unit);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  transition: all 0.2s ease-in-out;
  &:hover,
  &:focus {
    transform: scale(1.02);
    ${CardImgWrapper} {
      filter: brightness(80%);
    }
  }
  & > * {
    position: relative;
  }
  &.placeholder {
    background-color: var(--c-neutral-300);
  }
  &.placeholder:before {
    content: '';
    position: absolute;
    inset: 0;
    display: block;
    background-image: url(${placeholderURL});
    background-size: cover;
    border-radius: var(--double-unit);
    opacity: 0.4;
    filter: invert(1) brightness(1.5);
  }
`;

const PriceWrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: stretch;
`;

type DumbAmountProps = {
  main?: string;
  exponent?: string | null;
};

const DumbAmount = ({ main, exponent }: DumbAmountProps) => {
  return (
    <>
      <Text16 bold>{main || EMPTY}</Text16>
      <Caption color="var(--c-neutral-600)">{exponent || EMPTY}</Caption>
    </>
  );
};

const ConvertedAmount = ({ amount }: { amount: MonetaryAmountParams }) => {
  const converted = useAmountWithConversion({ monetaryAmount: amount });
  return <DumbAmount {...converted} />;
};

const Amount = ({ amount }: { amount?: MonetaryAmountParams }) => {
  if (!amount) return <DumbAmount />;
  return <ConvertedAmount amount={amount} />;
};

type Props = {
  to?: To;
  displayName?: string;
  position?: string;
  pictureUrl?: string;
  amount?: MonetaryAmountParams;
  auctionCount?: number;
  auctionEndingSoonest?: {
    amount: MonetaryAmountParams;
    endDate: Date;
  };
  buyNowMessage?: MessageDescriptor;
  readOnly?: boolean;
  onClick?: () => void;
};

export const EmptySlot = ({
  to,
  displayName = '',
  position,
  pictureUrl,
  amount,
  auctionCount,
  auctionEndingSoonest,
  buyNowMessage = glossary.startingAt,
  readOnly,
  onClick,
}: Props) => {
  const { isIosApp } = useIsMobileApp();

  const { now } = useTickerContext();
  const { endDate } = auctionEndingSoonest || {};
  const isWithinNextHour =
    endDate && isWithinInterval(endDate, { start: now, end: addHours(now, 1) });

  return (
    <StyledLink as={to && !isIosApp ? Link : 'div'} to={to} onClick={onClick}>
      <Wrapper className={!pictureUrl ? 'placeholder' : undefined}>
        {displayName && position && (
          <MissingCard displayName={displayName} position={position} />
        )}
        {pictureUrl && (
          <CardImgWrapper>
            <CardImg width={320} src={pictureUrl} alt={displayName} />
          </CardImgWrapper>
        )}
        {!readOnly && (
          <Vertical gap={1.5}>
            {auctionCount && !isWithinNextHour && (
              <LabelS as="p" color="var(--c-neutral-800)">
                <FormattedMessage
                  id="Collections.EmptySlot.auctionCount"
                  defaultMessage="{auctionCount, plural, one {1 live auction} other {# live auctions }}"
                  values={{ auctionCount }}
                />
              </LabelS>
            )}
            {isWithinNextHour && (
              <Vertical gap={0.5} center>
                <LabelS as="p" color="var(--c-red-800)">
                  <FormattedMessage
                    id="Collections.EmptySlot.auctionEndingSoon"
                    defaultMessage="Auction ending soon"
                    values={{ auctionCount }}
                  />
                </LabelS>
                <LabelS as="div" color="var(--c-red-800)">
                  <TimeLeft
                    time={endDate}
                    // Layout={expiring ? ExpiringLayout : undefined}
                    withExplicitTime
                  />
                </LabelS>
              </Vertical>
            )}
            <PriceWrapper>
              <Caption color="var(--c-neutral-600)">
                <FormattedMessage {...buyNowMessage} />
              </Caption>
              <Amount amount={amount} />
            </PriceWrapper>
          </Vertical>
        )}
      </Wrapper>
      {!readOnly && !isIosApp && (
        <Button color="tertiary" size="small">
          <FormattedMessage {...glossary.find} />
        </Button>
      )}
    </StyledLink>
  );
};
